<template>
  <div class="app-shell" :style="themeStyle">
    <!-- Layers: full-bleed variant art, then the scrim that guarantees contrast for the compliance strip. -->
    <div class="art" :style="{ backgroundImage: `url(${variant.background})` }" aria-hidden="true"></div>
    <div class="scrim" :style="variant.scrim ? { background: variant.scrim } : null" aria-hidden="true"></div>

    <div class="page cptpl_page">
      <!-- Evina ComplianceProtect markers, inside cptpl_page as in Evina's template; landing_page_id is
           issued by Evina per page (empty until then). -->
      <input type="hidden" name="cptpl_template" class="test cptpl_template" :value="brandConfig.evina.template" />
      <input type="hidden" name="cptpl_landing_page_id" class="test cptpl_landing_page_id" :value="landingPageId" />

      <header class="page__header">
        <!-- One plain img, absolute same-domain URL: Evina downloads this file to whitelist the logo. -->
        <img class="page__logo cptpl_logo cptpl_logo_static" :src="logoUrl" :alt="brandConfig.assets.logoAlt" crossorigin="anonymous" />
        <div class="page__header-end">
          <span v-if="mock" class="pill">{{ translations.mockBadge }}</span>
          <!-- Compliance exit (cptpl_exit): a close cross top right, as approved with Telkom and Evina. -->
          <button
            v-if="showClose"
            class="page__close cptpl_exit"
            type="button"
            data-apex-cta="cancel"
            :aria-label="translations.cancelCta"
            @click="subscription.cancel(brandConfig)"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
      </header>

      <!-- The pre-lander has no ladder; it appears with JOIN (design "Turn 4"). -->
      <StepLadder v-if="showLadder" :complete="route.name === 'thank-you'" />

      <router-view />
    </div>

    <!-- ?debug: outside cptpl_page, so Evina's compliance check never sees it (inside, the JOIN tap
         scored 8101 and the post-tap check blocked the subscribe). -->
    <div v-if="debug && route.name === 'landing'" class="debug-dock"><HeDebug /></div>

    <!-- Local preview only: switch brand and variant without changing the hosting origin. -->
    <p v-if="mock" class="preview">
      {{ translations.previewLabel }}:
      <a v-for="id in previewBrands" :key="id" class="text-link" :class="{ 'is-active': id === brand }" :href="previewHref({ brand: id, v: '' })">{{ id }}</a>
      ·
      <a v-for="id in Object.keys(brandConfig.variants)" :key="id" class="text-link" :class="{ 'is-active': id === variantId }" :href="previewHref({ v: id })">{{ id }}</a>
    </p>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useBrandStore } from '@/stores/brandStore'
import { useSubscriptionStore } from '@/stores/subscriptionStore'
import { Brands } from '@/brand'
import StepLadder from '@/components/StepLadder.vue'
import HeDebug from '@/components/HeDebug.vue'
import userAuthenticate from '@/functions/userAuthenticate'
import resolveVariant from '@/functions/variant'
import { isMockMode } from '@/services/telkomPremiumService'
import { complianceProtectTest } from '@/functions/evina'

const { brand, brandConfig, translations, themeStyle } = storeToRefs(useBrandStore())
const route = useRoute()
// Mock badge and preview bar; hidden for Evina's review (?cp=1) so the design they whitelist is the live one.
const mock = isMockMode() && !complianceProtectTest()
const debug = new URLSearchParams(window.location.search).has('debug')
const previewBrands = [Brands.RushGames, Brands.BattleLeague, Brands.VsGaming, Brands.Morabaraba]

const variantId = resolveVariant(brandConfig.value)
const variant = brandConfig.value.variants[variantId]
const landingPageId = computed(() => brandConfig.value.evina.landingPageIds[variantId] || '')
const logoUrl = computed(() => new URL(brandConfig.value.assets.logo, window.location.origin).href)
const subscription = useSubscriptionStore()
// The close cross belongs to the subscribe page; the confirmation and error screens have their own CTA.
const showClose = computed(() => route.name === 'landing' && !subscription.cancelled)
// Hidden from the first frame (before the router has resolved the route) until the pre-lander ends.
const showLadder = computed(() => !subscription.prelanding || route.name === 'thank-you' || route.name === 'error')

function previewHref(changes) {
  const params = new URLSearchParams(window.location.search)
  Object.entries(changes).forEach(([key, value]) => (value ? params.set(key, value) : params.delete(key)))
  return `/?${params.toString()}`
}

onMounted(userAuthenticate)
</script>

<style scoped>
.app-shell {
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  color: var(--color-text);
  font-family: var(--font-family);
  background: var(--color-bg);
}

.art {
  position: fixed;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: var(--color-bg);
}

.scrim {
  position: fixed;
  inset: 0;
  background: var(--scrim);
}

/* Content column: fills the viewport, grows (and scrolls) only if a small screen cannot fit it. */
.page {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: var(--app-max-width);
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0 auto;
  padding: calc(env(safe-area-inset-top, 0px) + 18px) 20px calc(env(safe-area-inset-bottom, 0px) + var(--page-bottom));
}

.page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page__logo {
  height: var(--logo-height);
  width: auto;
}

.page__header-end {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 44px tap target, drawn small and quiet so it does not compete with JOIN. */
.page__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: -8px -12px -8px 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
}

.page__close:hover,
.page__close:focus-visible {
  color: #fff;
}

.page__close svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.25;
  stroke-linecap: round;
}

.debug-dock {
  position: relative;
  z-index: 1;
  max-width: var(--app-max-width);
  margin: 0 auto;
  padding: 0 20px 16px;
}

.preview {
  position: relative;
  z-index: 1;
  padding: 8px 20px 16px;
  text-align: center;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
}

.preview a {
  margin-left: 6px;
}

.preview a.is-active {
  color: #fff;
  font-weight: 700;
}
</style>
